import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, config, Subject, Subscription } from 'rxjs';
import { ActionState } from 'src/app/enums/action-state.enum';
import { ModalDeleteConfigData } from 'src/app/models/modalDeleteConfigData';
import { StationsRepo } from 'src/app/repositories/stations/repo/station-repo';
import { AlertService } from 'src/app/services';
import { Station, StationState, ApplicationFeature } from 'src/swagger-gen/web-secured/models';
import { AlertParameters, AlertType } from '../../models/models-index';
import { ModalDeleteStationConfigComponent } from '../modal-delete-station-config/modal-delete-station-config.component';
import { DataAfterCloseDeleteStationConfigModal } from '../modal-delete-station-config/models/dataAfterCloseDeleteStationConfigModal';

@Component({
    selector: 'app-configuration-station-gir',
    templateUrl: './configuration-station-gir.component.html',
    styleUrls: ['./configuration-station-gir.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfigurationStationGirComponent implements OnInit {
  @Input() index: number;
  @Input() config: any;
  @Input() creationView: boolean;
  @Input() station: Station;
  @Output() reloadConfigGirEvent = new EventEmitter<any>();

  FEATURE = ApplicationFeature;
  destroyed$ = new Subject<void>();

  stationState = StationState;

  formStatus = new BehaviorSubject(ActionState.READY);
  actionState = ActionState;
  subscriptions = new Subscription();
  successMessage: any;
  isEditting = true;

  configForm = this.fb.group({
    name: ['', Validators.required],
    externalSiteId: ['', Validators.required],
    externalZoneId: ['', Validators.required]
  });
  girUid: string;
  eUid: string;
  isCollapsed = true;

  get f() {
    return this.configForm.controls;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private stationsRepo: StationsRepo,
    private translate: TranslateService,
    private alertService: AlertService,
    private logger: NGXLogger,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.patchStationValue(this.config);
    this.eUid = this.station?.uid;
    if (!this.creationView && !this.config.newConnection) {
      this.configForm.disable();
      this.isEditting = false;
    }
  }

  // Patch value from H2 station
  patchStationValue(config: any) {
    if (config) {
      this.configForm.patchValue({
        uid: config.uid,
        name: config.name || '',
        state: config.state,
        externalSiteId: config.externalSiteId,
        externalZoneId: config.externalZoneId
      });
      this.girUid = config.uid;
    }
  }

  toggleEdition(isCancel?: boolean) {
    this.isEditting = !this.isEditting;
    this.configForm.disabled
      ? this.configForm.enable()
      : this.configForm.disable();

    if (isCancel) {
      this.patchStationValue(this.config);
    }
  }

  onSubmit() {
    this.configForm.markAllAsTouched();
    if (this.configForm.valid) {
      this.formStatus.next(ActionState.LOADING);
      const formValue = this.configForm.value;

      const body = this.preparingBodyToRequest(formValue);

      // Update existing GIR connection
      if (this.girUid) {
        this.stationsRepo.updateConfigurationGir(this.eUid, this.girUid, body).toPromise()
          .then((response: any) => {
            this.formStatus.next(ActionState.READY);
            this.reloadConfigGirEvent.next(this.translate.instant('stations.form.message.success.config_updated'));
          })
          .catch(error => {
            this.formStatus.next(ActionState.READY);
            this.showAlert(error);
          });
      } else {
        // Create new GIR connection
        this.stationsRepo.createConfigurationGir(this.eUid, body).toPromise()
          .then((response: any) => {
            this.formStatus.next(ActionState.READY);
            this.reloadConfigGirEvent.next(this.translate.instant('stations.form.message.success.config_created'));
          })
          .catch(error => {
            this.formStatus.next(ActionState.READY);
            this.showAlert(error);
          });
      }
    }
  }

  preparingBodyToRequest(formValue: any): any {
    let body: any;
    body = {
      name: formValue.name,
      externalSiteId: formValue.externalSiteId,
      externalZoneId: formValue.externalZoneId
    };
    return body;
  }

  showAlert(error: any) {
    const title = this.translate.instant('error.title');
    const alertParams = new AlertParameters(false, false, true, false, false, false, true, title);
    this.alertService.showAlert(error, AlertType.ERROR, alertParams);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onDeleteConfig(): void {
    this.openDeleteDialog();
  }

  openDeleteDialog() {
    let dialogRef: any;
    const data = new ModalDeleteConfigData();
    data.type = 'gir';
    data.config = this.config;
    data.stationUid = this.station.uid;
    const configDialog = {
      data: data
    };
    dialogRef = this.dialog.open(ModalDeleteStationConfigComponent, configDialog);

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((result: DataAfterCloseDeleteStationConfigModal) => {
        if (result?.successDelete) {
          this.formStatus.next(ActionState.READY);
          this.reloadConfigGirEvent.next(this.translate.instant('stationConfig.delete.success'));
        }
      });
    }
    return;
  }
}
